import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { Autocomplete, TextField, CircularProgress, AutocompleteProps } from '@mui/material';
import { useDataProvider } from 'react-admin';
import { debounce } from 'lodash';

interface AsyncAutocompleteProps<T> {
  resource: string;
  label: string;
  value: T | T[] | null;
  onChange: (value: T | T[] | null) => void;
  multiple?: boolean;
  getOptionLabel: (option: T) => string;
  isOptionEqualToValue?: (option: T, value: T) => boolean;
  filters?: Record<string, any>;
  searchFilterKey?: string;
  sort?: { field: string; order: 'ASC' | 'DESC' };
  perPage?: number;
  required?: boolean;
  disabled?: boolean;
  getOptionDisabled?: (option: T) => boolean;
  helperText?: string;
  renderOption?: AutocompleteProps<T, boolean, boolean, boolean>['renderOption'];
  renderTags?: AutocompleteProps<T, boolean, boolean, boolean>['renderTags'];
}

function AsyncAutocomplete<T extends { id: string | number }>({
  resource,
  label,
  value,
  onChange,
  multiple = false,
  getOptionLabel,
  isOptionEqualToValue,
  filters = {},
  searchFilterKey = 'search',
  sort = { field: 'name', order: 'ASC' },
  perPage = 20,
  required = false,
  disabled = false,
  getOptionDisabled,
  helperText,
  renderOption,
  renderTags,
}: AsyncAutocompleteProps<T>) {
  const dataProvider = useDataProvider();
  const [options, setOptions] = useState<T[]>([]);
  const [loading, setLoading] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [totalCount, setTotalCount] = useState(0);
  const loadingRef = useRef(false);
  const initialLoadRef = useRef(false);
  const dataProviderRef = useRef(dataProvider);
  const resourceRef = useRef(resource);
  const perPageRef = useRef(perPage);
  const sortRef = useRef(sort);
  const filtersRef = useRef(filters);
  const searchFilterKeyRef = useRef(searchFilterKey);

  useEffect(() => {
    dataProviderRef.current = dataProvider;
    resourceRef.current = resource;
    perPageRef.current = perPage;
    sortRef.current = sort;
    filtersRef.current = filters;
    searchFilterKeyRef.current = searchFilterKey;
  }, [dataProvider, resource, perPage, sort, filters, searchFilterKey]);

  const loadOptions = useCallback(async (search: string, pageNum: number, append: boolean = false) => {
    if (loadingRef.current) return;

    loadingRef.current = true;
    setLoading(true);

    try {
      const searchFilters = search
        ? { ...filtersRef.current, [searchFilterKeyRef.current]: search }
        : filtersRef.current;

      // @ts-ignore
      const { data, total } = await dataProviderRef.current.getList(resourceRef.current, {
        pagination: { page: pageNum, perPage: perPageRef.current },
        sort: sortRef.current,
        filter: searchFilters,
      });

      if (append) {
        setOptions((prev) => [...prev, ...data]);
      } else {
        setOptions(data);
      }

      setTotalCount(total || 0);
      setHasMore(data.length === perPageRef.current);
    } catch (error) {
      console.error(`Error loading ${resourceRef.current}:`, error);
      if (!append) {
        setOptions([]);
      }
    } finally {
      setLoading(false);
      loadingRef.current = false;
    }
  }, []);

  const valueRef = useRef(value);
  const [loadedValue, setLoadedValue] = useState<T | T[] | null>(null);
  const [loadingValue, setLoadingValue] = useState(false);

  useEffect(() => {
    valueRef.current = value;
  }, [value]);

  // Buscar objetos completos quando value contém IDs primitivos
  useEffect(() => {
    const fetchValueObjects = async () => {
      if (!value) {
        setLoadedValue(null);
        return;
      }

      const isPrimitive = (val: any): val is string | number =>
        typeof val === 'string' || typeof val === 'number';

      // Verificar se value contém IDs primitivos
      if (Array.isArray(value)) {
        // Array de valores
        if (value.length === 0) {
          setLoadedValue([]);
          return;
        }

        // Se todos são primitivos, buscar os objetos
        if (value.every(isPrimitive)) {
          setLoadingValue(true);
          try {
            const { data } = await dataProvider.getMany(resource, {
              ids: value as unknown as (string | number)[],
            });
            setLoadedValue(data as T[]);
            // Adicionar aos options se não existirem
            setOptions((prev) => {
              const newOptions = [...prev];
              data.forEach((item: any) => {
                if (!newOptions.find(opt => opt.id === item.id)) {
                  newOptions.push(item);
                }
              });
              return newOptions;
            });
          } catch (error) {
            console.error(`Error loading values for ${resource}:`, error);
            setLoadedValue([]);
          } finally {
            setLoadingValue(false);
          }
          return;
        }

        // Se são objetos, usar diretamente
        setLoadedValue(value);
      } else {
        // Valor único
        if (isPrimitive(value)) {
          setLoadingValue(true);
          try {
            const { data } = await dataProvider.getMany(resource, {
              ids: [value],
            });
            if (data && data.length > 0) {
              setLoadedValue(data[0] as T);
              // Adicionar aos options se não existir
              setOptions((prev) => {
                if (!prev.find(opt => opt.id === data[0].id)) {
                  return [data[0], ...prev];
                }
                return prev;
              });
            } else {
              setLoadedValue(null);
            }
          } catch (error) {
            console.error(`Error loading value for ${resource}:`, error);
            setLoadedValue(null);
          } finally {
            setLoadingValue(false);
          }
          return;
        }

        // Se é objeto, usar diretamente
        setLoadedValue(value);
      }
    };

    fetchValueObjects();
  }, [value, dataProvider, resource]);

  const debouncedLoadOptions = useMemo(
    () =>
      debounce((search: string) => {
        setPage(1);
        setHasMore(true);
        // Clear options but keep selected values
        const currentValue = loadedValue || valueRef.current;
        if (!currentValue) {
          setOptions([]);
        } else if (Array.isArray(currentValue)) {
          setOptions(currentValue.filter((v: any) => v && v.id));
        } else if ((currentValue as any).id) {
          setOptions([currentValue as T]);
        } else {
          setOptions([]);
        }
        loadOptions(search, 1, false);
      }, 300),
    [loadOptions, loadedValue]
  );

  useEffect(() => {
    if (!initialLoadRef.current) {
      initialLoadRef.current = true;
      loadOptions('', 1, false);
    }
  }, []);

  useEffect(() => {
    return () => {
      debouncedLoadOptions.cancel();
    };
  }, [debouncedLoadOptions]);

  const handleInputChange = (_: any, newInputValue: string, reason: string) => {
    if (reason === 'reset') {
      return;
    }
    setInputValue(newInputValue);
    debouncedLoadOptions(newInputValue);
  };

  const handleScroll = (event: React.SyntheticEvent) => {
    const listboxNode = event.currentTarget;
    const position = listboxNode.scrollTop + listboxNode.clientHeight;
    const bottom = listboxNode.scrollHeight - 10;

    if (position >= bottom && hasMore && !loading && !loadingRef.current && options.length > 0) {
      const nextPage = page + 1;
      setPage(nextPage);
      loadOptions(inputValue, nextPage, true);
    }
  };

  useEffect(() => {
    // Usar loadedValue ao invés de value para garantir que temos objetos completos
    const valueToCheck = loadedValue || value;

    if (valueToCheck && !Array.isArray(valueToCheck)) {
      const exists = options.find(opt => opt.id === (valueToCheck as any).id);
      if (!exists && (valueToCheck as any).id) {
        setOptions((prev) => {
          // Check again to avoid duplicates in case of race condition
          if (prev.find(opt => opt.id === (valueToCheck as any).id)) return prev;
          return [valueToCheck as T, ...prev];
        });
      }
    } else if (Array.isArray(valueToCheck) && valueToCheck.length > 0) {
      const missingOptions = valueToCheck.filter((v: any) => v && v.id && !options.find(opt => opt.id === v.id));
      if (missingOptions.length > 0) {
        setOptions((prev) => {
          // Filter out any that may have been added in the meantime
          const reallyMissing = missingOptions.filter((mo: any) => !prev.find(opt => opt.id === mo.id));
          if (reallyMissing.length === 0) return prev;
          return [...reallyMissing, ...prev];
        });
      }
    }
  }, [loadedValue, value, options]);

  // Usar loadedValue se disponível, caso contrário usar value diretamente
  const displayValue = loadedValue !== null ? loadedValue : value;

  return (
    <Autocomplete
      multiple={multiple as any}
      fullWidth
      disabled={disabled}
      getOptionDisabled={getOptionDisabled}
      options={options}
      value={displayValue}
      onChange={(_, newValue) => onChange(newValue)}
      onInputChange={handleInputChange}
      getOptionLabel={getOptionLabel}
      isOptionEqualToValue={isOptionEqualToValue || ((option, val) => option.id === val.id)}
      loading={loading || loadingValue}
      ListboxProps={{
        onScroll: handleScroll,
        style: { maxHeight: '300px' },
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          required={required}
          helperText={helperText}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {(loading || loadingValue) ? <CircularProgress color="inherit" size={20} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
      renderOption={renderOption}
      renderTags={renderTags}
      filterOptions={(x) => x}
      noOptionsText={(loading || loadingValue) ? 'Carregando...' : 'Nenhuma opção encontrada'}
    />
  );
}

export default AsyncAutocomplete;
